export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

export interface ToastMessage {
  id: string
  message: string
  variant: ToastVariant
  // 0 means sticky: no auto-dismiss, closes only on user action.
  duration: number
  // Full text offered behind a "Copy" affordance when the on-screen message is a
  // short summary of something longer (e.g. an error's stack). Absent = no copy button.
  copyText?: string
}

// Per-call overrides. `error` supplies the rich value to copy while `message`
// stays the short on-screen line; `copyText` sets that copy text directly.
export interface ToastOptions {
  duration?: number
  error?: unknown
  copyText?: string
}

type Listener = (toasts: ToastMessage[]) => void

let _toasts: ToastMessage[] = []
const _listeners = new Set<Listener>()

function _notify() {
  _listeners.forEach(l => l([..._toasts]))
}

// Serialize any thrown value into full copyable text (name, message, stack).
function _formatError(error: unknown): string {
  if (error instanceof Error) {
    return error.stack || `${error.name}: ${error.message}`
  }
  if (typeof error === 'string') return error
  try {
    return JSON.stringify(error, null, 2)
  } catch {
    return String(error)
  }
}

function _add(message: string, variant: ToastVariant, opts: ToastOptions, defaultDuration: number) {
  const id = crypto.randomUUID()
  const duration = opts.duration ?? defaultDuration
  const copyText = opts.copyText ?? (opts.error !== undefined ? _formatError(opts.error) : undefined)
  _toasts = [..._toasts, { id, message, variant, duration, copyText }]
  _notify()
  // No timer here: the ToastItem component owns the dismiss countdown so it can
  // pause on hover. Sticky toasts (duration 0) are dismissed only by the user.
  // The countdown lives in the mounted component, so ToastContainer must stay
  // mounted for the toast's whole life or a non-sticky toast never gets reaped.
}

function _remove(id: string) {
  _toasts = _toasts.filter(t => t.id !== id)
  _notify()
}

export function subscribe(listener: Listener) {
  _listeners.add(listener)
  listener([..._toasts])
  return () => { _listeners.delete(listener) }
}

// Non-error variants default to an 8s auto-dismiss. Errors default to sticky
// (duration 0) so they can't vanish before being read -- dismiss is explicit.
export const toast = {
  success: (message: string, opts: ToastOptions = {}) => _add(message, 'success', opts, 8000),
  error:   (message: string, opts: ToastOptions = {}) => _add(message, 'error',   opts, 0),
  warning: (message: string, opts: ToastOptions = {}) => _add(message, 'warning', opts, 8000),
  info:    (message: string, opts: ToastOptions = {}) => _add(message, 'info',    opts, 8000),
  dismiss: _remove,
}

// Exposed globally: the bus is an app-wide singleton, and toasts are
// display-only with no authority, so a global handle carries no risk.
declare global {
  interface Window {
    toast: typeof toast
  }
}
if (typeof window !== 'undefined') {
  window.toast = toast
}
